import { createReducer, on } from "@ngrx/store";
import { DocViewerState } from "../doc-viewer.state";
import { RedactionEditorState } from "@store/doc-viewer/redaction-editor/redaction-editor.state";
import { currentRedactionActions, redactionEditorActions } from "./redaction-editor.actions";
import { cloneDeep } from "lodash-es";
import { RedactionStatus } from "@models/redaction-status.enum";
import { LifeCycleEdit } from "../../../components/review/redaction-annotation/life-cycle-edit.enum";
import { RedactionAnnotationEdit } from "src/app/components/review/redaction-annotation/redaction-annotation-edit.model";
import { ComparisonUtility } from "@utilities/comparison.utility";
import { highlightColorIdToHighlightColorMap } from "@models/mappers/maps/highlight-color-id-to-highlight-color.map";

export const initialState: RedactionEditorState = {
    pageNumber: 0,
    totalPages: 0,
    fileIdList: {},
    currentRedactionId: 0,
    hasUnsavedRedactionChanges: false,
    redactions: [],
    colors: [],
    isProcessing: false,
    status: RedactionStatus.Unknown,
    redactionText: 'Redacted',
    redactionColorId: '69b9cfdb-6f1a-4631-bb51-d1d5a30ce1aa'
}

export const redactionEditorReducer = createReducer<DocViewerState['redactionEditor']>(
    initialState,
    on(redactionEditorActions.initialize, (_state, { status, totalPages }) => {
        const clone = cloneDeep(_state);
        clone.status = status;
        clone.isProcessing = false;
        clone.totalPages = totalPages;
        clone.pageNumber = 1;
        return clone;
    }),
    on(redactionEditorActions.redactionColorsLoaded, (_state, { colors }) => {
        const clone = cloneDeep(_state);
        clone.colors = colors;
        return clone;
    }),
    on(redactionEditorActions.updateRedactionsForCurrentPage, (_state, { redactions }) => {
        const clone = cloneDeep(_state);
        if (!ComparisonUtility.isEqual(clone.redactions, redactions)) {
            clone.hasUnsavedRedactionChanges = true;
        }
        const updatedRedactions = cloneDeep(redactions).map((updatedRedaction) => {
            if (updatedRedaction.status === LifeCycleEdit.Initialized) {
                updatedRedaction.fileId = _state.fileIdList[_state.pageNumber];
                updatedRedaction.text = _state.redactionText;
                updatedRedaction.highlightColorId = _state.redactionColorId;
                updatedRedaction.highlightColor = highlightColorIdToHighlightColorMap.get(_state.redactionColorId);
                updatedRedaction.rank = _state.pageNumber;
                updatedRedaction.status = LifeCycleEdit.Created;
            }
            return updatedRedaction;
        });

        updatedRedactions.forEach((updatedRedaction) => {
            const existingRedactionIndex = clone.redactions.findIndex(existingRedaction => existingRedaction.clientId === updatedRedaction.clientId);
            if (existingRedactionIndex >= 0) {
                clone.redactions[existingRedactionIndex] = updatedRedaction;
            } else {
                clone.redactions.push(updatedRedaction);
            }
        });
        return clone;
    }),
    on(redactionEditorActions.markEditorDirty, (_state) => {
        const clone = cloneDeep(_state);
        clone.hasUnsavedRedactionChanges = true;
        return clone;
    }),
    on(redactionEditorActions.processingStarted, (_state) => {
        const clone = cloneDeep(_state);
        clone.isProcessing = true;
        return clone;
    }),
    on(redactionEditorActions.processingStopped, (_state, { status }) => {
        const clone = cloneDeep(_state);
        clone.isProcessing = false;
        clone.status = status;
        return clone;
    }),
    on(redactionEditorActions.markEditorClean, (_state) => {
        const clone = cloneDeep(_state);
        clone.hasUnsavedRedactionChanges = false;
        return clone;
    }),
    on(redactionEditorActions.redactionsLoaded, (_state, { redactions }) => {
        const clone = cloneDeep(_state);
        clone.redactions = cloneDeep(redactions);
        return clone;
    }),
    on(redactionEditorActions.goToPage, (_state, { pageNum }) => {
        const clone = cloneDeep(_state);
        clone.pageNumber = pageNum;
        return clone;
    }),
    on(currentRedactionActions.setCurrentRedaction, (_state, { redactionId }) => {
        const clone = cloneDeep(_state);
        clone.currentRedactionId = redactionId;
        return clone;
    }),
    on(redactionEditorActions.createImage, (_state) => {
        const clone = cloneDeep(_state);
        clone.status = RedactionStatus.ImagesCreationRequested;
        clone.isProcessing = true;
        return clone;
    }),
    on(redactionEditorActions.imageCreated, (_state) => {
        const clone = cloneDeep(_state);
        clone.status = RedactionStatus.HasImages;
        clone.isProcessing = false;
        return clone;
    }),
    on(redactionEditorActions.createRedactedPDF, (_state) => {
        const clone = cloneDeep(_state);
        clone.status = RedactionStatus.PdfCreationRequested;
        clone.isProcessing = true;
        clone.hasUnsavedRedactionChanges = false;
        return clone;
    }),
    on(redactionEditorActions.redactedPDFCreated, (_state) => {
        const clone = cloneDeep(_state);
        clone.status = RedactionStatus.HasPdf;
        clone.isProcessing = false;
        return clone;
    }),
    on(currentRedactionActions.createRedaction, (_state, { redaction }) => {
        const clone = cloneDeep(_state);
        const newEdit = new RedactionAnnotationEdit(redaction, _state.redactions.length);
        newEdit.fileId = _state.fileIdList[_state.pageNumber];
        newEdit.text = _state.redactionText;
        newEdit.highlightColorId = _state.redactionColorId;
        newEdit.highlightColor = highlightColorIdToHighlightColorMap.get(_state.redactionColorId);
        newEdit.rank = _state.pageNumber;
        newEdit.status = LifeCycleEdit.Initialized
        clone.redactions = [...clone.redactions, newEdit];
        clone.hasUnsavedRedactionChanges = true;
        return clone;
    }),
    on(currentRedactionActions.updateRedaction, (_state, { redaction }) => {
        const clone = cloneDeep(_state);
        const targetIndex = clone.redactions.findIndex(r => r.clientId === redaction.clientId);
        if (targetIndex !== -1) {
            const redactionClone = cloneDeep(redaction);
            if (redactionClone.status === LifeCycleEdit.Initialized)
                redactionClone.status = LifeCycleEdit.Created;
            else if (redactionClone.status != LifeCycleEdit.Created)
                redactionClone.status = LifeCycleEdit.Modified;
            clone.redactions[targetIndex] = redactionClone;
            clone.hasUnsavedRedactionChanges = true;
            return clone;
        }
        return clone;
    }),
    on(currentRedactionActions.deleteRedaction, (_state, { redactionId }) => {
        const clone = cloneDeep(_state);
        const targetIndex = clone.redactions.findIndex(r => r.clientId === redactionId);
        if (targetIndex === -1)
            return clone;

        const redaction = clone.redactions[targetIndex];
        if (redaction.status === LifeCycleEdit.Created)
            clone.redactions.splice(targetIndex, 1);
        else
            clone.redactions[targetIndex].status = LifeCycleEdit.Deleted;
        clone.hasUnsavedRedactionChanges = true;
        return clone;
    }),
    on(redactionEditorActions.setFileIdList, (_state, { items }) => {
        const clone = cloneDeep(_state);
        clone.fileIdList = items;
        return clone;
    }),
    on(redactionEditorActions.redactionStatusLoaded, (_state, { status }) => {
        const clone = cloneDeep(_state);
        clone.status = status;
        return clone;
    }),
    on(currentRedactionActions.setRedactionText, (_state, { text }) => {
        const clone = cloneDeep(_state);
        clone.redactionText = text;
        return clone;
    }),
    on(currentRedactionActions.setColorId, (_state, { colorId }) => {
        const clone = cloneDeep(_state);
        clone.redactionColorId = colorId;
        return clone;
    })
);
